import React, { useState, useEffect } from 'react';
import './pStyles/About.css';
import { Users, Target, HeartHandshake, Earth, ArrowRight } from 'lucide-react';

interface ImpactItem {
  id: number;
  title: string;
  description: string;
  stat: string;
  statLabel: string;
}

interface ValueItem {
  icon: React.ComponentType<{ size: number; className?: string }>;
  title: string;
  desc: string;
  color: string;
  delay: string;
}

interface TeamMember {
  initial: string;
  name: string;
  role: string;
  bio: string;
  delay: string;
  image: string;
}

interface VisibilityState {
  [key: string]: boolean;
}

const About: React.FC = () => {
  const [isVisible, setIsVisible] = useState<VisibilityState>({});

  // Enhanced intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

const impactData: ImpactItem[] = [
  {
    id: 1,
    title: 'Social Enterprise Incubation',
    description: 'Connecting skilled professionals to launch sustainable businesses that drive community change. From reusable nappy laundromats to eco-friendly services, we empower entrepreneurs to create both impact and income.',
    stat: '45+',
    statLabel: 'Social Enterprises Launched'
  },
  {
    id: 2,
    title: 'Essential Donations Program',
    description: 'Providing critical resources directly to families in need. Our nappy packs, hygiene kits, and sustainable baby supplies support vulnerable communities while reducing environmental waste.',
    stat: '12,500+',
    statLabel: 'Care Packs Distributed'
  },

];

  const valuesData: ValueItem[] = [
    { icon: Users, title: 'Community First', desc: 'Prioritizing community needs in all decisions', color: '#E85D4F', delay: '0.1s' },
    { icon: HeartHandshake, title: 'Inclusivity', desc: 'Welcoming all backgrounds and ensuring equal participation', color: '#F39C42', delay: '0.2s' },
    { icon: Earth, title: 'Sustainability', desc: 'Creating long-term solutions for people and environment', color: '#85a56c', delay: '0.3s' },
    { icon: Target, title: 'Impact Driven', desc: 'Focusing on transparent and measurable outcomes', color: '#2D7A71', delay: '0.4s' }
  ];

  const teamMembers: TeamMember[] = [
    { 
      initial: 'L', 
      name: 'Prof. Leanne Scott, PhD', 
      role: 'Co-founder', 
      bio: 'Expert in problem structuring and data-driven, community-led initiatives with a passion for sustainable impact.',
      delay: '0.2s',
      image: require('../img/s200_leanne.scott.jpg')
    },
    { 
      initial: 'I', 
      name: 'Dr. Isabel Meyer, PhD', 
      role: 'Co-founder', 
      bio: 'Researcher and operations specialist focused on designing development interventions for sustained community impact.',
      delay: '0.4s',
      image: require('../img/isabelmeyer.jpg')
    }
  ];

  // Using the provided images from the upload
  const impactImages: string[] = [
    'https://images.unsplash.com/photo-1530099486328-e021101a494a?w=500&h=300&fit=crop', // Crafting tools - for skills development
    'https://images.pexels.com/photos/6994982/pexels-photo-6994982.jpeg', // People working at computers - technical training
    'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=500&h=300&fit=crop' // Team unity hands together
  ];

  return (
    <div className="about-page-new">
      {/* ENHANCED HERO SECTION - Solid Teal Background with Image */}
      <section className="about-hero-enhanced">
        {/* Hero Image on Left Side */}
        <div className="hero-image-container">
          <img
            src="https://images.pexels.com/photos/6647026/pexels-photo-6647026.jpeg"
            alt="Volunteers working together in meeting"
            className="hero-side-image"
          />
        </div>

        {/* Hero Content on Right Side */}
        <div className="hero-content-wrapper">
          <div className="about-hero-content">
            <h1 className="about-title-enhanced">
              Our Story is <span className="highlight-white">Community</span>
            </h1>
            <p className="about-subtitle-enhanced">
              SkillsBridge was born from a simple idea: that everyone has something to give, and every community has the potential to thrive. We're here to build the bridge between them.
            </p>
            <button className="hero-cta-button">
              Join Our Mission
              <ArrowRight size={20} />
            </button>
          </div>
        </div>

      
      </section>

      {/* MISSION & VISION - Enhanced Layout */}
      <section className="mission-vision-section">
        <div className="container-new">
          <div className="mission-vision-grid">
            <div 
              data-animate 
              id="mission"
              className={`mission-content ${isVisible.mission ? 'animate-visible' : ''}`}
            >
              <div className="mission-icon-wrapper">
                <Target size={32} />
              </div>
              <h2 className="section-title-new">Our Mission</h2>
              <p className="section-text">
                To connect passionate individuals with meaningful volunteer opportunities that address critical community needs, ensuring every contribution creates lasting impact in health, education, and social welfare.
              </p>
            </div>

            <div 
              data-animate 
              id="vision"
              className={`vision-content ${isVisible.vision ? 'animate-visible' : ''}`}
            >
              <div className="vision-icon-wrapper">
                <Earth size={32} />
              </div>
              <h2 className="section-title-new">Our Vision</h2>
              <p className="section-text">
                We envision thriving communities where every individual can contribute their unique skills toward collective wellbeing, fostering a culture of care, collaboration, and sustainable development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES SECTION - Enhanced with Icons */}
      <section className="values-section-new">
        <div className="container-new">
          <div data-animate id="values-title" className={`values-title-wrapper ${isVisible['values-title'] ? 'animate-visible' : ''}`}>
            <h2 className="section-title-new">Our Core Values</h2>
            <p className="section-subtitle">The principles that guide everything we do</p>
          </div>

          <div className="values-grid-new">
            {valuesData.map((value, idx) => {
              const IconComponent = value.icon;
              return (
                <div
                  key={idx}
                  data-animate
                  id={`value-${idx}`}
                  className={`value-item-new ${isVisible[`value-${idx}`] ? 'animate-visible' : ''}`}
                  style={{ animationDelay: value.delay }}
                >
                  <div 
                    className="value-icon-new"
                    style={{ background: `linear-gradient(135deg, ${value.color}, ${value.color}dd)` }}
                  >
                    <IconComponent size={32} />
                  </div>
                  <h3 className="value-title-new">{value.title}</h3>
                  <p className="value-text-new">{value.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* IMPACT GALLERY - Enhanced with Real Images */}
      <section className="impact-gallery-section">
        <div className="container-new">
          <div data-animate id="impact-title" className={`gallery-intro ${isVisible['impact-title'] ? 'animate-visible' : ''}`}>
            <h2 className="section-title-new">Our Impact in Action</h2>
            <p>See how SkillsBridge volunteers are making real differences in communities around the world</p>
          </div>

          <div className="impact-gallery">
            {impactData.map((item, idx) => (
              <div
                key={item.id}
                data-animate
                id={`impact-${idx}`}
                className={`impact-card ${isVisible[`impact-${idx}`] ? 'animate-visible' : ''}`}
                style={{ animationDelay: `${idx * 0.15}s` }}
              >
                <div className="impact-image-wrapper">
                  <img
                    src={impactImages[idx]}
                    alt={item.title}
                    className="impact-image-real"
                  />
                  <div className="impact-image-overlay"></div>
                </div>
                <div className="impact-content">
                  <h3 className="impact-title">{item.title}</h3>
                  <p className="impact-description">{item.description}</p>
                  <div className="impact-stat">
                    <div className="impact-stat-number">{item.stat}</div>
                    <div className="impact-stat-label">{item.statLabel}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM SECTION - Enhanced with Better Layout */}
      <section className="team-section-new">
        <div className="container-new">
          <div data-animate id="team-title" className={`team-title-wrapper ${isVisible['team-title'] ? 'animate-visible' : ''}`}>
            <h2 className="section-title-new">Our Leadership</h2>
            <p className="section-subtitle">Meet the visionaries behind SkillsBridge</p>
          </div>

          <div className="team-grid-new">
            {teamMembers.map((member, idx) => (
              <div
                key={idx}
                data-animate
                id={`team-${idx}`}
                className={`team-member-new ${isVisible[`team-${idx}`] ? 'animate-visible' : ''}`}
                style={{ animationDelay: member.delay }}
              >
                <div className="member-avatar-new">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="avatar-photo"
                  />
                </div>
                <h3 className="member-name-new">{member.name}</h3>
                <p className="member-role-new">{member.role}</p>
                <p className="member-bio-new">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CALL TO ACTION - New Section */}
      <section className="cta-section">
        <div className="container-new">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Make a Difference?</h2>
            <p className="cta-subtitle">
              Join thousands of volunteers who are transforming communities through meaningful action.
            </p>
            <div className="cta-buttons">
              <button className="cta-button-primary">Become a Volunteer</button>
              <button className="cta-button-secondary">Start a Project</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;